import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '@/config/firebase';
import type {
    DataUpdateGroupId,
    DpAttachment,
    HealthPlanAction,
    HealthPlanProvider,
    HealthPlanSnapshot,
    OvertimeCompensationType,
    TicketCategory,
} from '../../../../shared/dpRequests';

const functions = getFunctions(app, 'us-central1');

export interface DataUpdateGroupInfo {
    id: DataUpdateGroupId;
    label: string;
    requiresAttachment: boolean;
}

export interface EditableProfileResponse {
    collaboratorId: string;
    collaboratorName: string;
    values: Record<string, unknown>;
    groups: DataUpdateGroupInfo[];
}

export interface DataUpdatePayload {
    groups: string[];
    values: Record<string, unknown>;
    attachments: DpAttachment[];
}

export interface DayOffPayload {
    managerEmail: string;
    reason?: string;
    clientEmails: string[];
    noClient: boolean;
}

export interface RequestContext {
    collaboratorName: string;
    contractRegime: string;
    projects: string[];
    experienceEndMonth: string | null;
    isClt: boolean;
    todayIso: string;
    responsibleEmail: string | null;
}

export interface OvertimePayload {
    projectName: string;
    internalProject: boolean;
    clientManagerEmails: string[];
    raroManagerEmail: string;
    date: string;
    startTime: string;
    endTime: string;
    reason: string;
    compensationType: OvertimeCompensationType;
    attachments: DpAttachment[];
}

export interface HealthPlanPayload {
    action: HealthPlanAction;
    provider: HealthPlanProvider | null;
    includeHolder: boolean;
    dependentNames: string[];
    note?: string;
    attachments: DpAttachment[];
}

export interface MyHealthPlanResponse {
    snapshot: HealthPlanSnapshot;
    hasOpenRequest: boolean;
}

export interface MyDayOffStatus {
    eligible: boolean;
    birthday: string | null;
    issue: 'weekend' | 'holiday' | 'vacation' | null;
}

export type DayOffClientState = 'pending' | 'already_approved' | 'already_rejected' | 'expired' | 'closed' | 'not_found';

export interface DayOffClientView {
    state: DayOffClientState;
    collaboratorName?: string;
    date?: string;
}

interface RequestIdResponse {
    requestId: string;
}

const getMyEditableProfileFn = httpsCallable<void, EditableProfileResponse>(functions, 'getMyEditableProfile');
const createDataUpdateFn = httpsCallable<DataUpdatePayload, RequestIdResponse>(functions, 'createDataUpdateRequest');
const resubmitDataUpdateFn = httpsCallable<DataUpdatePayload & { requestId: string }, RequestIdResponse>(functions, 'resubmitDataUpdateRequest');
const cancelDataUpdateFn = httpsCallable<{ requestId: string }, RequestIdResponse>(functions, 'cancelDataUpdateRequest');
const decideDataUpdateFn = httpsCallable<{ requestId: string; action: 'approve' | 'correct' | 'reject'; note?: string }, { requestId: string; status: string }>(functions, 'decideDataUpdateRequest');

const createTicketFn = httpsCallable<{ category: TicketCategory; subject: string; body: string; attachments: DpAttachment[] }, RequestIdResponse>(functions, 'createDpTicket');
const postTicketMessageFn = httpsCallable<{ requestId: string; body: string; attachments: DpAttachment[] }, RequestIdResponse>(functions, 'postDpTicketMessage');
const resolveTicketFn = httpsCallable<{ requestId: string }, RequestIdResponse>(functions, 'resolveDpTicket');
const cancelTicketFn = httpsCallable<{ requestId: string }, RequestIdResponse>(functions, 'cancelDpTicket');

const createDayOffFn = httpsCallable<DayOffPayload, { requestId: string; date: string }>(functions, 'createDayOffRequest');
const cancelDayOffFn = httpsCallable<{ requestId: string }, RequestIdResponse>(functions, 'cancelDayOffRequest');
const markDayOffRegisteredFn = httpsCallable<{ requestId: string }, RequestIdResponse>(functions, 'markDayOffRegistered');
const decideDayOffFn = httpsCallable<{ requestId: string; action: 'approve' | 'reject'; note?: string }, { requestId: string; status: string }>(functions, 'decideDayOffRequest');

export async function fetchEditableProfile(): Promise<EditableProfileResponse> {
    return (await getMyEditableProfileFn()).data;
}

export async function submitDataUpdate(payload: DataUpdatePayload): Promise<string> {
    return (await createDataUpdateFn(payload)).data.requestId;
}

export async function resubmitDataUpdate(requestId: string, payload: DataUpdatePayload): Promise<void> {
    await resubmitDataUpdateFn({ requestId, ...payload });
}

export async function cancelDataUpdate(requestId: string): Promise<void> {
    await cancelDataUpdateFn({ requestId });
}

export async function decideDataUpdate(input: { requestId: string; action: 'approve' | 'correct' | 'reject'; note?: string }): Promise<void> {
    await decideDataUpdateFn(input);
}

export async function submitTicket(input: { category: TicketCategory; subject: string; body: string; attachments: DpAttachment[] }): Promise<string> {
    return (await createTicketFn(input)).data.requestId;
}

export async function postTicketMessage(input: { requestId: string; body: string; attachments: DpAttachment[] }): Promise<void> {
    await postTicketMessageFn(input);
}

export async function resolveTicket(requestId: string): Promise<void> {
    await resolveTicketFn({ requestId });
}

export async function cancelTicket(requestId: string): Promise<void> {
    await cancelTicketFn({ requestId });
}

export async function submitDayOff(input: DayOffPayload): Promise<{ requestId: string; date: string }> {
    return (await createDayOffFn(input)).data;
}

export async function cancelDayOff(requestId: string): Promise<void> {
    await cancelDayOffFn({ requestId });
}

export async function markDayOffRegistered(requestId: string): Promise<void> {
    await markDayOffRegisteredFn({ requestId });
}

export async function decideDayOff(input: { requestId: string; action: 'approve' | 'reject'; note?: string }): Promise<void> {
    await decideDayOffFn(input);
}

const getRequestContextFn = httpsCallable<void, RequestContext>(functions, 'getMyRequestContext');
const createOvertimeFn = httpsCallable<OvertimePayload, RequestIdResponse>(functions, 'createOvertimeRequest');
const cancelOvertimeFn = httpsCallable<{ requestId: string }, RequestIdResponse>(functions, 'cancelOvertimeRequest');
const decideOvertimeFn = httpsCallable<{ requestId: string; action: 'register' | 'reject'; note?: string }, RequestIdResponse>(functions, 'decideOvertimeRequest');

const createRaroInvestFn = httpsCallable<{ referenceMonth: string; note?: string; attachments: DpAttachment[] }, RequestIdResponse>(functions, 'createRaroInvestRequest');
const cancelRaroInvestFn = httpsCallable<{ requestId: string }, RequestIdResponse>(functions, 'cancelRaroInvestRequest');
const decideRaroInvestFn = httpsCallable<{ requestId: string; action: 'approve' | 'reject'; note?: string }, RequestIdResponse>(functions, 'decideRaroInvestRequest');
const markRaroInvestReimbursedFn = httpsCallable<{ requestId: string }, RequestIdResponse>(functions, 'markRaroInvestReimbursed');

const getMyHealthPlanFn = httpsCallable<void, MyHealthPlanResponse>(functions, 'getMyHealthPlan');
const createHealthPlanFn = httpsCallable<HealthPlanPayload, RequestIdResponse>(functions, 'createHealthPlanRequest');
const cancelHealthPlanFn = httpsCallable<{ requestId: string }, RequestIdResponse>(functions, 'cancelHealthPlanRequest');
const decideHealthPlanFn = httpsCallable<{ requestId: string; action: 'approve' | 'reject'; note?: string; effectiveDate?: string }, RequestIdResponse>(functions, 'decideHealthPlanRequest');

const getDayOffClientFn = httpsCallable<{ token: string }, DayOffClientView>(functions, 'getDayOffClientApproval');
const respondDayOffClientFn = httpsCallable<{ token: string; approve: boolean; note?: string }, { result: string }>(functions, 'respondDayOffAsClient');

export async function fetchRequestContext(): Promise<RequestContext> {
    return (await getRequestContextFn()).data;
}

export async function submitOvertime(payload: OvertimePayload): Promise<string> {
    return (await createOvertimeFn(payload)).data.requestId;
}

export async function cancelOvertime(requestId: string): Promise<void> {
    await cancelOvertimeFn({ requestId });
}

export async function decideOvertime(input: { requestId: string; action: 'register' | 'reject'; note?: string }): Promise<void> {
    await decideOvertimeFn(input);
}

export async function submitRaroInvest(input: { referenceMonth: string; note?: string; attachments: DpAttachment[] }): Promise<string> {
    return (await createRaroInvestFn(input)).data.requestId;
}

export async function cancelRaroInvest(requestId: string): Promise<void> {
    await cancelRaroInvestFn({ requestId });
}

export async function decideRaroInvest(input: { requestId: string; action: 'approve' | 'reject'; note?: string }): Promise<void> {
    await decideRaroInvestFn(input);
}

export async function markRaroInvestReimbursed(requestId: string): Promise<void> {
    await markRaroInvestReimbursedFn({ requestId });
}

export async function fetchMyHealthPlan(): Promise<MyHealthPlanResponse> {
    return (await getMyHealthPlanFn()).data;
}

export async function submitHealthPlan(payload: HealthPlanPayload): Promise<string> {
    return (await createHealthPlanFn(payload)).data.requestId;
}

export async function cancelHealthPlan(requestId: string): Promise<void> {
    await cancelHealthPlanFn({ requestId });
}

export async function decideHealthPlan(input: { requestId: string; action: 'approve' | 'reject'; note?: string; effectiveDate?: string }): Promise<void> {
    await decideHealthPlanFn(input);
}

export async function fetchDayOffClientView(token: string): Promise<DayOffClientView> {
    return (await getDayOffClientFn({ token })).data;
}

export async function respondDayOffClient(input: { token: string; approve: boolean; note?: string }): Promise<string> {
    return (await respondDayOffClientFn(input)).data.result;
}

const getMyDayOffStatusFn = httpsCallable<void, MyDayOffStatus>(functions, 'getMyDayOffStatus');

export async function fetchMyDayOffStatus(): Promise<MyDayOffStatus> {
    return (await getMyDayOffStatusFn()).data;
}
